import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import { shopItemForStoreStateSelectorFamily } from '@shared/api/shopItem/shotItem.selector';
import {
  createShopOrder,
  deleteShopOrderById,
} from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderCreateDto } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole, UserRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PaymentResultOverlay from '../components/PaymentResultOverlay';
import PaymentWidgetModal from '../components/PaymentWidgetModal';
import PhraseInfoInputs from '../components/PhraseInfoInputs';
import ReceiverInfoInputs from '../components/ReceiverInfoInputs';
import SenderInfoInputs from '../components/SenderInfoInputs';
import ShopItemCounter from '../components/ShopItemCounter';
import { CustomerCallHelperBox } from '../containers/CustomerCallCenterBox/CustomerCallHelperBox';
import {
  TOSS_CHECKOUT_FORM_KEY,
  useShopTossCheckout,
} from '../hooks/useShopTossCheckout';
import { useStoreTitleHook } from '../hooks/useStoreTitleHook';
import { MAX_RETRY, useTossPaymentResult } from '../hooks/useTossPaymentResult';
import { IShopItemFormData, ShopItemFormData } from '../state/shopItemCheckout.interface';
import { getEstimateDeliveredAt } from '../utils/getEstimateDeliveredAt';
import { getShopOrderDto } from '../utils/getShopOrderDto';

const ShopItemTossCheckoutPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 부고 정보
  const bugoId = searchParams.get('bugoId');
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  // 주문 상태
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [isOrderCreating, setIsOrderCreating] = useState(false);

  useStoreTitleHook('주문/결제', ' ');
  const { storeRole, userProfile, signupOrLoginStoreGuest, getMe } = useAuth();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  // 상품 정보
  const { itemId } = useParams();
  const { data: shopItemData, status: shopItemQueryStatus } = useRecoilValue(
    shopItemForStoreStateSelectorFamily(itemId ?? ''),
  );

  // Form
  const { register, control, handleSubmit, setValue, getValues, reset } =
    useForm<IShopItemFormData>();
  const itemCount = useWatch({
    control,
    name: ShopItemFormData.ItemCount,
    defaultValue: 1,
  });
  const usedPoint = useWatch({
    control,
    name: ShopItemFormData.UsedPoint,
    defaultValue: 0,
  });

  // 결제 금액 계산
  const totalAmount = shopItemData ? shopItemData.priceRetail * itemCount - usedPoint : 0;

  // 토스페이먼츠 결제 위젯 훅
  const {
    isReady,
    isModalOpen: isPaymentWidgetModalOpen, // 결제위젯 모달 오픈상태
    isSubmitting,
    openModal: openPaymentWidgetModal,
    closeModal,
    requestModalPayment: requestPayment,
  } = useShopTossCheckout({ amount: totalAmount });

  // 재시도 한도 초과 시 새 결제 흐름 시작 ref (순환 참조 방지)
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onRestartPaymentRef = useRef<() => void>(() => {});
  const onRestartPaymentStable = useCallback(() => onRestartPaymentRef.current(), []);
  const {
    isConfirming,
    confirmError,
    retryCount,
    handleRetry,
    handleCancelOrder,
    resetResult,
  } = useTossPaymentResult({ bugoId, onRestartPayment: onRestartPaymentStable });

  // 폼 데이터 sessionStorage 복원 (마운트 시 1회)
  useEffect(() => {
    const saved = sessionStorage.getItem(TOSS_CHECKOUT_FORM_KEY);
    if (saved) {
      try {
        const { itemId: savedItemId, data } = JSON.parse(saved);
        if (savedItemId === itemId) {
          reset(data);
        }
      } catch {
        // JSON 파싱 실패 시 무시
      }
      sessionStorage.removeItem(TOSS_CHECKOUT_FORM_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 부고 데이터로 수령인 정보 설정
  useEffect(() => {
    if (bugoData) {
      setValue(
        ShopItemFormData.ReceiverAddress,
        (bugoData.fevent.funeralHomeInfoEmbed?.address ??
          bugoData.fevent.funeralHomeInfo.address) +
          ', [' +
          bugoData.fevent.funeralHomeInfo.name +
          ', ' +
          (bugoData.fevent.roomInfoEmbed?.name ?? bugoData.fevent.roomInfo?.name ?? '') +
          ']',
      );
      setValue(ShopItemFormData.ReceiverName, bugoData.member.fullName + ' 상주님');
    }
  }, [bugoData, setValue]);

  // 폼 제출 → 유효성 검사 → guest 가입 → 주문 생성 → 모달 열기
  const onSubmit: SubmitHandler<IShopItemFormData> = useCallback(async () => {
    if (isOrderCreating) return;

    const formData = getValues();

    if (!formData.senderName || !formData.senderTel) {
      notificateError(notificationInstance, '주문자 정보를 입력해주세요.');
      return;
    }
    if (!shopItemData || !bugoData) {
      notificateError(notificationInstance, '상품 혹은 부고정보가 올바르지 않습니다.');
      return;
    }
    if (totalAmount <= 0) {
      notificateError(notificationInstance, '결제 금액이 올바르지 않습니다.');
      return;
    }

    setIsOrderCreating(true);

    try {
      let userId = userProfile?._id;
      let role = storeRole();

      if (!userProfile || userProfile.roles.includes(UserRole.StoreGuest)) {
        const token = await signupOrLoginStoreGuest(
          {
            name: formData.senderName,
            phoneNumber: formData.senderTel,
            password: String(formData.senderTel).split('').slice(-4).join(''),
          },
          true,
        );
        const profile = await getMe(token);
        if (!profile?._id) return;
        userId = profile._id;
        role = profile.roles.includes(UserRole.StoreCustomer)
          ? LowerRole.StoreCustomer
          : LowerRole.StoreGuest;
      }

      if (!userId) return;

      const shopOrderDto: ShopOrderCreateDto = getShopOrderDto({
        formData,
        shopItem: shopItemData,
        bugo: bugoData,
        userId,
      });
      const { data: shopOrder } = await createShopOrder(shopOrderDto, role);
      setCreatedOrderId(shopOrder._id);
      openPaymentWidgetModal();
    } catch (err) {
      console.error(err);
      const message = (err as Error).message || '주문 처리 중 오류가 발생했습니다.';
      notificateError(notificationInstance, message);
    } finally {
      setIsOrderCreating(false);
    }
  }, [
    isOrderCreating,
    getValues,
    shopItemData,
    bugoData,
    totalAmount,
    userProfile,
    storeRole,
    signupOrLoginStoreGuest,
    getMe,
    notificationInstance,
    openPaymentWidgetModal,
  ]);

  // 재시도 한도 초과 → 새 결제 흐름 시작 (latest ref 패턴)
  onRestartPaymentRef.current = () => {
    // 기존 order 삭제 (fire-and-forget)
    if (confirmError?.orderId) {
      deleteShopOrderById(confirmError.orderId, storeRole()).catch(() => {
        // 삭제 실패 시 서버 스케줄러가 정리
      });
    }
    // overlay 닫기 + hook 상태 초기화
    resetResult();
    // URL 정리 (result/paymentKey 파라미터 제거)
    const cleanUrl = bugoId ? `?bugoId=${bugoId}` : window.location.pathname;
    navigate(cleanUrl, { replace: true });
    // 새 order 생성 → 위젯 모달 열기
    handleSubmit(onSubmit)();
  };

  // 모달 결제 버튼 → requestModalPayment
  const onModalPayment = useCallback(async () => {
    if (!createdOrderId || !shopItemData) return;
    const formData = getValues();

    await requestPayment({
      orderId: createdOrderId,
      orderName: shopItemData.shopItemInfo.name,
      customerName: formData.senderName,
      customerMobilePhone: formData.senderTel,
      formData,
      shopItemId: shopItemData._id,
    });
  }, [createdOrderId, shopItemData, getValues, requestPayment]);

  // 모달 닫기 → 주문 삭제 (사용자 명시적 취소)
  const onModalClose = useCallback(() => {
    if (createdOrderId) {
      deleteShopOrderById(createdOrderId, storeRole()).catch(() => {
        // 삭제 실패 시 서버 스케줄러가 정리
      });
    }
    setCreatedOrderId(null);
    closeModal();
  }, [createdOrderId, storeRole, closeModal]);

  // 모달 뒤로가기 처리 (history.pushState / popstate)
  const modalHistoryPushedRef = useRef(false);

  useEffect(() => {
    if (isPaymentWidgetModalOpen && !modalHistoryPushedRef.current) {
      modalHistoryPushedRef.current = true;
      history.pushState({ modal: true }, '');
    }

    if (!isPaymentWidgetModalOpen && modalHistoryPushedRef.current) {
      modalHistoryPushedRef.current = false;
      // popstate로 닫힌 경우가 아니면 history entry 제거
      if (history.state?.modal) {
        history.back();
      }
    }
  }, [isPaymentWidgetModalOpen]);

  useEffect(() => {
    const handlePopState = () => {
      if (modalHistoryPushedRef.current) {
        modalHistoryPushedRef.current = false;
        onModalClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onModalClose]);

  return (
    <div>
      {searchParams.get('result') ? (
        // redirect 복귀 — PaymentResultOverlay가 처리할 때까지 로딩만 표시
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      ) : shopItemQueryStatus === 'success' && shopItemData ? (
        <form onSubmit={handleSubmit(onSubmit)} className="relative pb-12">
          <div className="p-4 text-sm">
            {/* 화환 정보 */}
            <div className="flex justify-between border-b border-gray-300 p-2 pt-0">
              <div className="flex">
                <img
                  src={fileBucketUrl(shopItemData.shopItemInfo.image.url, 'w400')}
                  alt="Flower Image"
                  className="aspect-square w-20 object-cover object-center"
                />
                <div className="pl-2">
                  <h4 className="font-bold">{shopItemData.shopItemInfo.name}</h4>
                  <div className="pt-1 text-xs theme-text-main">
                    {formatDate(getEstimateDeliveredAt(), {
                      dateSeparater: '/',
                      contains: {
                        year: false,
                        day: true,
                      },
                    })}
                    도착 보장
                  </div>
                  <div className="w-[84px] pt-4">
                    <ShopItemCounter
                      control={control}
                      name={ShopItemFormData.ItemCount}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-auto text-right">
                <div className="text-sm leading-4 text-gray-500 line-through">
                  {formatPrice(shopItemData.priceRetailShow * itemCount)}원
                </div>
                <div className="flex items-end">
                  <h3 className="text-xl font-bold leading-6">
                    {formatPrice(shopItemData.priceRetail * itemCount)}
                  </h3>
                  <span className="text-sm leading-6">원</span>
                </div>
              </div>
            </div>
            {/* Contents */}
            <div className="space-y-4 py-4">
              {/* 주문자 정보 */}
              <SenderInfoInputs
                register={register}
                control={control}
                names={{
                  senderName: ShopItemFormData.SenderName,
                  senderPassword: ShopItemFormData.SenderPassword,
                  senderTel: ShopItemFormData.SenderTel,
                }}
              />
              {/* 수령인 정보 */}
              <ReceiverInfoInputs
                register={register}
                names={{
                  receiverName: ShopItemFormData.ReceiverName,
                  receiverAddress: ShopItemFormData.ReceiverAddress,
                }}
              />
              {/* 문구 작성 */}
              <PhraseInfoInputs
                register={register}
                control={control}
                names={{
                  phraseSender: ShopItemFormData.PhraseSender,
                  phraseCondolence: ShopItemFormData.PhraseCondolence,
                }}
              />
              {/* 결제 정보 */}
              <div>
                <h4 className="mb-3 font-bold">결제 정보</h4>
                <div className="flex flex-col space-y-3 px-4 py-3 theme-bg-1">
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">상품 금액 ({itemCount}개)</span>
                    <span>{formatPrice(shopItemData.priceRetailShow * itemCount)}원</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">할인 금액</span>
                    <span className="text-myTeal">
                      {formatPrice(
                        (shopItemData.priceRetail - shopItemData.priceRetailShow) *
                          itemCount,
                      )}
                      원
                    </span>
                  </div>
                  {usedPoint !== 0 && (
                    <div className="flex items-center justify-between">
                      <span className="theme-text-8">포인트사용</span>
                      <span className="text-myTeal">-{formatPrice(usedPoint)}원</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="theme-text-8">배송비</span>
                    <div className="flex items-center justify-end gap-x-2">
                      <div className="theme-text-main">
                        {formatDate(getEstimateDeliveredAt(), {
                          dateSeparater: '/',
                          contains: {
                            year: false,
                            day: true,
                          },
                        })}
                        도착 보장
                      </div>
                      <span>무료배송</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* 동의 확인 */}
              <div>
                <div className="flex justify-between">
                  <h4 className="pb-3 font-bold">개인정보 취급방침에 대한 동의</h4>
                </div>
                <p className="p-4 pt-2 theme-text-8 theme-bg-1">
                  <span className="flex h-12 items-center justify-start py-2 text-sm">
                    아래 내용을 확인하였으며 결제에 동의합니다.
                  </span>
                  <span className="block font-bold leading-7 underline">
                    개인정보 취급방침
                  </span>
                  <span className="whitespace-pre-line break-keep text-xs leading-[18px]">
                    가온프라임(주)는 통신판매중개자로서, 입점판매자가 등록한 상품 정보 및
                    거래에 대해 가온프라임(주)는 일체의 책임을 지지 않습니다.
                  </span>
                </p>
              </div>
              <CustomerCallHelperBox />
            </div>
          </div>
          {/* 결제 박스 */}
          <div className="fixed left-0 right-0 bottom-0 mx-auto flex max-w-screen-md items-center gap-x-6 border-t py-2 px-2 theme-bg-1 theme-border-5">
            <div className="pl-4 text-right text-xs">
              <div className="theme-text-8">총 할인금액</div>
              <div className="font-bold text-myTeal">
                {formatPrice(
                  -1 *
                    itemCount *
                    (shopItemData.priceRetailShow - shopItemData.priceRetail),
                )}
                원
              </div>
            </div>
            <Button
              type="submit"
              disabled={isOrderCreating}
              className="button-rectangle flex-1 text-white theme-bg-main disabled:opacity-50"
            >
              {isOrderCreating
                ? '주문 처리 중...'
                : `${formatPrice(totalAmount)}원 결제하기`}
            </Button>
          </div>
        </form>
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}

      {/* 결제 결과 오버레이 */}
      <PaymentResultOverlay
        isConfirming={isConfirming}
        confirmError={confirmError}
        retryCount={retryCount}
        maxRetry={MAX_RETRY}
        onRetry={handleRetry}
        onCancel={handleCancelOrder}
      />

      {/* 결제 수단 선택 모달 */}
      <PaymentWidgetModal
        isOpen={isPaymentWidgetModalOpen}
        isReady={isReady}
        isSubmitting={isSubmitting}
        shopItem={shopItemData ?? null}
        itemCount={itemCount}
        totalAmount={totalAmount}
        onPay={onModalPayment}
        onClose={onModalClose}
      />
    </div>
  );
};

export default ShopItemTossCheckoutPage;
